import { useState } from "react";
import { Plus, Pencil, Trash2, Syringe } from "lucide-react";
import {
  useImmunizations,
  useDeleteImmunization,
  type ImmunizationSummary,
} from "../../lib/api/useImmunizations";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { ImmunizationDialog } from "./ImmunizationDialog";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { SectionCard } from "../ui/SectionCard";
import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { EmptyState } from "../ui/EmptyState";
import { SECTION_ACCENT } from "../../lib/accents";
import { INLINE_STATUS, ROW } from "../../lib/surfaces";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";

export function ImmunizationsList({ childId }: { childId: string }) {
  const { data: immunizations, isLoading, error } = useImmunizations(childId);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ImmunizationSummary | undefined>();
  const [deleting, setDeleting] = useState<ImmunizationSummary | undefined>();
  const deleteMutation = useDeleteImmunization(childId);
  const { publish } = useToast();

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.id);
      publish({ variant: "success", title: "Immunization deleted successfully" });
      setDeleting(undefined);
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to delete immunization",
        description: err instanceof ApiError ? err.message : undefined,
      });
      setDeleting(undefined);
    }
  };

  return (
    <SectionCard
      icon={Syringe}
      title="Immunizations"
      accent={SECTION_ACCENT.medical}
      actions={
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add Immunization
        </Button>
      }
    >
      {isLoading && (
        <div role="status" className={INLINE_STATUS}>
          <Spinner size="sm" />
          Loading…
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error.message}</p>}

      {!isLoading && !error && (immunizations?.length ?? 0) === 0 && (
        <EmptyState
          size="sm"
          accent="violet"
          icon={Syringe}
          title="No immunizations recorded"
          description="Add vaccination records so caregivers and clinics have an accurate, up-to-date history."
        />
      )}

      {!isLoading && !error && immunizations && immunizations.length > 0 && (
        <ul className="space-y-3">
          {immunizations.map((imm) => (
            <li key={imm.id} className={ROW}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-neutral-900">{imm.vaccineName}</h3>
                    {imm.dateAdministered && (
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                        Given {imm.dateAdministered}
                      </span>
                    )}
                    {imm.nextDueDate && (
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                        Next due {imm.nextDueDate}
                      </span>
                    )}
                  </div>
                  {imm.provider && (
                    <p className="mt-1 text-sm text-neutral-700">{imm.provider}</p>
                  )}
                  {imm.notes && (
                    <p className="mt-2 text-xs italic text-neutral-600">{imm.notes}</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(imm)}
                    aria-label={`Edit ${imm.vaccineName}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-neutral-900",
                      FOCUS_RING,
                    )}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(imm)}
                    aria-label={`Delete ${imm.vaccineName}`}
                    className={cn(
                      "rounded-full p-1.5 text-neutral-600 transition hover:bg-white hover:text-red-700",
                      FOCUS_RING,
                    )}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ImmunizationDialog open={createOpen} onOpenChange={setCreateOpen} childId={childId} />

      <ImmunizationDialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(undefined)}
        childId={childId}
        immunization={editing}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete immunization"
        description={`Are you sure you want to delete this record${deleting ? ` "${deleting.vaccineName}"` : ""}?`}
        confirmLabel="Delete"
        destructive
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
      />
    </SectionCard>
  );
}
